'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { useParams, useRouter, useSearchParams } from 'next/navigation'
import { signIn } from 'next-auth/react'
import type { Mode } from '@core/types'
import type { Locale } from '@configs/i18n'
import { ADMIN_ROLE_ID, SUPERVISOR_ROLE_ID } from '@/libs/auth'
import { useSettings } from '@core/hooks/useSettings'
import { getLocalizedUrl } from '@/utils/i18n'
import { getRuntimeConfig } from '@/libs/runtime-config'
import themeConfig from '@configs/themeConfig'

type ErrorType = {
  message: string[] | string
}

type MarketingStats = {
  applications: number
  questions: number
  activeJobs: number
}

const Login = ({ mode: _mode }: { mode: Mode }) => {
  const [showPassword, setShowPassword] = useState(false)
  const [email, setEmail] = useState('admin@example.com')
  const [password, setPassword] = useState('12345678')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [errorState, setErrorState] = useState<ErrorType | null>(null)
  const [marketingStats, setMarketingStats] = useState<MarketingStats | null>(null)
  const [marketingStatsLoading, setMarketingStatsLoading] = useState(true)

  const router = useRouter()
  const searchParams = useSearchParams()
  const { lang: locale } = useParams()
  const { settings } = useSettings()

  const forgotHref = getLocalizedUrl('/forgot-password', locale as Locale)
  const registerHref = getLocalizedUrl('/register', locale as Locale)

  useEffect(() => {
    const base = getRuntimeConfig('NEXT_PUBLIC_API_URL', 'http://localhost:5000').replace(/\/$/, '')
    const ctrl = new AbortController()
    setMarketingStatsLoading(true)
    fetch(`${base}/public/marketing-stats`, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error('stats'))))
      .then((data: MarketingStats) => {
        if (
          data &&
          typeof data.applications === 'number' &&
          typeof data.questions === 'number' &&
          typeof data.activeJobs === 'number'
        ) {
          setMarketingStats(data)
        }
      })
      .catch(() => { })
      .finally(() => setMarketingStatsLoading(false))
    return () => ctrl.abort()
  }, [])

  const localeTag = locale === 'ar' ? 'ar-SA' : 'en-US'
  const formatStat = (value: number | undefined) =>
    value === undefined ? '—' : value.toLocaleString(localeTag)

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault()
    setErrorState(null)
    setIsSubmitting(true)

    try {
      const res = await signIn('credentials', {
        email: email.trim(),
        password,
        redirect: false,
      })

      if (res && res.ok && res.error === null) {
        let session: Awaited<ReturnType<typeof import('next-auth/react').getSession>> = null

        try {
          const { getSession } = await import('next-auth/react')
          session = await getSession()

          if (session && (session as any).backend_access_token) {
            const { setApiToken } = await import('@/libs/api')
            setApiToken((session as any).backend_access_token)
          }
        } catch (err) {
          console.error('Error storing backend token:', err)
        }

        const redirectFromQuery = searchParams.get('redirectTo')
        const roleId = (session?.user as { role_id?: number })?.role_id
        const defaultPath =
          roleId === ADMIN_ROLE_ID ? '/dashboard' : roleId === SUPERVISOR_ROLE_ID ? '/jobs' : '/my-questionnaire'
        const redirectURL = redirectFromQuery ?? defaultPath

        router.replace(getLocalizedUrl(redirectURL, locale as Locale))
        router.refresh()
        return
      }

      if (res?.error) {
        try {
          const parsed = JSON.parse(res.error)
          const message = parsed.message
            ? Array.isArray(parsed.message)
              ? parsed.message
              : [parsed.message]
            : ['Login failed. Please check your credentials.']
          setErrorState({ message })
        } catch {
          setErrorState({ message: [res.error || 'Login failed. Please check your credentials.'] })
        }
      } else {
        setErrorState({ message: ['Login failed. Please check your credentials.'] })
      }
    } catch {
      setErrorState({ message: ['An unexpected error occurred. Please try again.'] })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className='flex min-h-screen'>
      <div
        className={`hidden lg:flex lg:w-[55%] flex-col justify-between p-12 relative overflow-hidden ${settings.skin === 'bordered' ? 'border-ie' : ''
          }`}
        style={{ background: 'linear-gradient(135deg, hsl(228, 35%, 14%) 0%, hsl(234, 50%, 22%) 50%, hsl(234, 85%, 35%) 100%)' }}
      >
        <div className='relative z-10'>
          <div className='flex items-center gap-3'>
            <img
              src='/images/logo.png'
              alt={`${themeConfig.templateName} logo`}
              className='h-11 w-11 rounded-xl object-contain'
            />
            <div>
              <h1 className='text-xl font-bold text-white'>{themeConfig.templateName}</h1>
              <p className='text-xs text-blue-200/90 tracking-wide'>{themeConfig.brandTagline}</p>
            </div>
          </div>
        </div>

        <div className='relative z-10 max-w-lg'>
          <h2 className='text-4xl font-bold text-white leading-tight mb-4'>Manage your hiring pipeline with confidence</h2>
          <p className='text-lg text-blue-200/80'>
            Track applications, manage job listings, and find the best candidates - all in one place.
          </p>
        </div>

        <div className='relative z-10 flex flex-wrap items-center gap-6 sm:gap-8'>
          <div className='text-center min-w-[4.5rem]'>
            <div
              className={`text-3xl font-bold text-white tabular-nums ${marketingStatsLoading ? 'animate-pulse opacity-80' : ''}`}
            >
              {marketingStatsLoading ? '…' : formatStat(marketingStats?.applications)}
            </div>
            <div className='text-sm text-blue-300'>Applications</div>
          </div>
          <div className='hidden h-10 w-px bg-white/20 sm:block' />
          <div className='text-center min-w-[4.5rem]'>
            <div
              className={`text-3xl font-bold text-white tabular-nums ${marketingStatsLoading ? 'animate-pulse opacity-80' : ''}`}
            >
              {marketingStatsLoading ? '…' : formatStat(marketingStats?.questions)}
            </div>
            <div className='text-sm text-blue-300'>Questions</div>
          </div>
          <div className='hidden h-10 w-px bg-white/20 sm:block' />
          <div className='text-center min-w-[4.5rem]'>
            <div
              className={`text-3xl font-bold text-white tabular-nums ${marketingStatsLoading ? 'animate-pulse opacity-80' : ''}`}
            >
              {marketingStatsLoading ? '…' : formatStat(marketingStats?.activeJobs)}
            </div>
            <div className='text-sm text-blue-300'>Active Jobs</div>
          </div>
        </div>

        <div className='absolute top-20 right-20 w-72 h-72 rounded-full bg-primary/10 blur-3xl' />
        <div className='absolute bottom-20 left-20 w-96 h-96 rounded-full bg-blue-500/5 blur-3xl' />
        <div className='absolute top-1/2 right-0 w-64 h-64 rounded-full bg-indigo-500/10 blur-3xl' />
      </div>

      <div className='flex flex-1 min-h-screen items-center justify-center bg-slate-100 px-4 py-10 sm:px-8'>
        <div className='w-full max-w-[440px]'>
          <div className='rounded-2xl border border-slate-200/90 bg-white p-8 shadow-[0_20px_50px_-12px_rgba(15,23,42,0.12)] sm:p-10'>
            <div className='mb-8 text-center sm:text-left'>
              <h2 className='text-[1.65rem] font-bold leading-tight tracking-tight text-slate-900 sm:text-3xl'>
                Welcome back! <span className='inline-block' aria-hidden>👋</span>
              </h2>
              <p className='mt-2 text-sm text-slate-500 sm:text-base'>Please sign in to your account to continue</p>
            </div>

            {errorState && (
              <div className='mb-6 rounded-xl border border-red-200 bg-red-50/90 px-4 py-3 text-sm text-red-800'>
                {(Array.isArray(errorState.message) ? errorState.message : [errorState.message]).map((msg, idx) => (
                  <p key={`${msg}-${idx}`}>{msg}</p>
                ))}
              </div>
            )}

            <form onSubmit={handleLogin} className='space-y-5'>
              <div className='space-y-2'>
                <label htmlFor='login-email' className='text-sm font-semibold text-slate-800'>
                  Email
                </label>
                <div className='relative'>
                  <i className='ri-mail-line pointer-events-none absolute left-3.5 top-1/2 z-[1] -translate-y-1/2 text-lg text-slate-400' />
                  <input
                    id='login-email'
                    type='email'
                    autoComplete='email'
                    placeholder='you@company.com'
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    className='h-12 w-full rounded-xl border border-slate-200 bg-white pl-11 pr-3.5 text-slate-900 shadow-sm outline-none transition-[border-color,box-shadow] placeholder:text-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-500/15'
                  />
                </div>
              </div>

              <div className='space-y-2'>
                <label htmlFor='login-password' className='text-sm font-semibold text-slate-800'>
                  Password
                </label>
                <div className='relative'>
                  <i className='ri-lock-2-line pointer-events-none absolute left-3.5 top-1/2 z-[1] -translate-y-1/2 text-lg text-slate-400' />
                  <input
                    id='login-password'
                    type={showPassword ? 'text' : 'password'}
                    autoComplete='current-password'
                    placeholder='Enter your password'
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    className='h-12 w-full rounded-xl border border-slate-200 bg-white pl-11 pr-11 text-slate-900 shadow-sm outline-none transition-[border-color,box-shadow] placeholder:text-slate-400 focus:border-blue-600 focus:ring-4 focus:ring-blue-500/15'
                  />
                  <button
                    type='button'
                    onClick={() => setShowPassword(!showPassword)}
                    className='absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700'
                    aria-label='Toggle password visibility'
                  >
                    <i className={showPassword ? 'ri-eye-off-line text-xl' : 'ri-eye-line text-xl'} />
                  </button>
                </div>
              </div>

              <div className='flex flex-wrap items-center justify-between gap-3'>
                {/* <label className='flex cursor-pointer items-center gap-2.5'>
                  <input type='checkbox' className='h-4 w-4 rounded border-slate-300 accent-blue-600 focus:ring-2 focus:ring-blue-500/20' />
                  <span className='text-sm text-slate-600'>Remember me</span>
                </label> */}
                <Link
                  href={forgotHref}
                  className='text-sm font-semibold text-blue-600 transition-colors hover:text-blue-700'
                >
                  Forgot password?
                </Link>
              </div>

              <button
                type='submit'
                disabled={isSubmitting}
                className='h-12 w-full cursor-pointer rounded-xl bg-blue-600 text-base font-semibold text-white shadow-md shadow-blue-600/25 transition-colors hover:bg-blue-700 active:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60'
              >
                {isSubmitting ? 'Logging in…' : 'Log In'}
              </button>

              {/* <p className='pt-1 text-center text-sm text-slate-500'>
                Don&apos;t have an account?{' '}
                <Link href={registerHref} className='font-semibold text-blue-600 transition-colors hover:text-blue-700'>
                  Sign up instead
                </Link>
              </p> */}
            </form>
          </div>
        </div>
      </div>
    </div>
  )
}

export default Login
